import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type {
  KioskSuggestionsModalBlockConfig,
  KioskSuggestionsModalBlockScenarioContent,
} from '@front-platform/content-builder';
import { kioskSuggestionsModalBlockResolveConfig } from '@front-platform/content-builder';
import {
  afterNextRender,
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  input,
  computed,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import { KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { isAnyKioskSuggestionsPreviewModalOpen } from '@core/services/cb2-preview/kiosk-suggestions-preview-modal.util';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import {
  previewScenarioForBloc$,
  resolvePreviewScenarioForBloc,
} from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { filter, map, merge } from 'rxjs';
import {
  KioskSuggestionsModalComponent,
  KioskSuggestionsModalConfig,
} from '../kiosk-suggestions-modal/kiosk-suggestions-modal.component';
export interface KioskSuggestionsModalBlockRuntimeConfig {
  'secondary_color'?: IContentBuilderFieldColor;
}

export type KioskSuggestionsModalBlockConfigInput = Partial<
  Omit<KioskSuggestionsModalBlockConfig, keyof KioskSuggestionsModalBlockRuntimeConfig> &
    KioskSuggestionsModalBlockRuntimeConfig
>;
export type KioskSuggestionsModalBlockResolvedConfig = Omit<
  KioskSuggestionsModalBlockConfig,
  keyof KioskSuggestionsModalBlockRuntimeConfig
> &
  Partial<KioskSuggestionsModalBlockRuntimeConfig>;
export interface KioskSuggestionsModalBlockContract extends ConfigComponentContract<
  KioskSuggestionsModalBlockConfigInput,
  KioskSuggestionsModalBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-suggestions-modal-block',
  templateUrl: './kiosk-suggestions-modal-block.component.html',
  standalone: false,
})
export class KioskSuggestionsModalBlockComponent
  implements OnInit, KioskSuggestionsModalBlockContract
{
  readonly config = input<KioskSuggestionsModalBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskSuggestionsModalBlockConfig,
    KioskSuggestionsModalBlockRuntimeConfig
  >(kioskSuggestionsModalBlockResolveConfig, () => ({ config: this.config() }));
  readonly heading = computed(() => this.resolvedConfig()['heading'] as unknown as string);
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonLabel = computed(() => this.resolvedConfig()['button_label'] as unknown as string);
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly suggestionsSet = computed(
    () => this.resolvedConfig()['suggestions_set'] as unknown as string,
  );

  private readonly modalsService: ModalsService<KioskSuggestionsModalConfig> = inject(
    ModalsService<KioskSuggestionsModalConfig>,
  );
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBlocKey = inject(CB2_PREVIEW_BLOC_KEY, { optional: true });
  private readonly previewScenario$ = previewScenarioForBloc$(
    KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT,
  );
  private readonly previewModalInputs = computed(() => ({
    heading: this.heading(),
    buttonBackgroundColor: this.buttonBackgroundColor(),
    buttonTextColor: this.buttonTextColor(),
    textColor: this.textColor(),
    secondaryColor: this.secondaryColor(),
    backgroundColor: this.backgroundColor(),
    cardColor: this.cardColor(),
    buttonLabel: this.buttonLabel(),
    suggestionsSet: this.suggestionsSet() ?? '',
  }));
  private hasOpened = false;

  constructor() {
    // Prop edits (title, colors) must refresh the already-open preview modal.
    // open() does not update an existing modal, so setData carries the new inputs.
    effect(() => {
      if (!this.cb2PreviewState.isActive()) {
        return;
      }
      const inputs = this.previewModalInputs();
      const content = previewScenarioData<KioskSuggestionsModalBlockScenarioContent>(
        resolvePreviewScenarioForBloc(
          this.cb2PreviewState.previewScenarios(),
          this.previewBlocKey,
          KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT,
        ),
        KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT,
      );
      if (!content?.isOpen) {
        return;
      }
      untracked(() =>
        this.publishPreviewModal(inputs, content.suggestions as unknown as DoodProductModel[]),
      );
    });

    merge(
      this.router.events.pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        map(() => 'navigation' as const),
      ),
    )
      .pipe(takeUntilDestroyed())
      .subscribe(source => {
        if (source === 'navigation') {
          this.modalsService.close('kiosk-suggestions-modal');
        }
      });

    // Open after the first render so signal inputs from the content builder are set.
    // Opening in ngOnInit can race with ComponentFactory setInput() and drop cardColor.
    afterNextRender(() => {
      if (this.cb2PreviewState.isActive()) {
        return;
      }
      this.openModal();
    });
  }

  ngOnInit() {
    if (this.cb2PreviewState.isActive()) {
      this.previewScenario$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(scenario => {
        const content = previewScenarioData<KioskSuggestionsModalBlockScenarioContent>(
          scenario,
          KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT,
        );
        if (content?.isOpen) {
          this.openModal(content.suggestions as unknown as DoodProductModel[]);
          return;
        }
        this.closeSharedPreviewModalIfIdle();
      });
    }
  }

  private openModal(previewProducts?: readonly DoodProductModel[]): void {
    const isPreview = this.cb2PreviewState.isActive();
    if (!isPreview && this.hasOpened) {
      return;
    }

    // Studio fixtures supply the products. A missing suggestions set must not hide the preview.
    if (!isPreview && !this.suggestionsSet()) {
      return;
    }

    if (!isPreview) {
      this.hasOpened = true;
      this.modalsService.open(KioskSuggestionsModalComponent.handle, undefined, {
        ...this.previewModalInputs(),
      });
      return;
    }

    this.publishPreviewModal(this.previewModalInputs(), previewProducts);
  }

  private publishPreviewModal(
    inputs: {
      heading: string;
      buttonBackgroundColor: IContentBuilderFieldColor;
      buttonTextColor: IContentBuilderFieldColor;
      textColor: IContentBuilderFieldColor;
      secondaryColor: IContentBuilderFieldColor;
      backgroundColor: IContentBuilderFieldColor;
      cardColor: IContentBuilderFieldColor;
      buttonLabel: string;
      suggestionsSet: string;
    },
    previewProducts?: readonly DoodProductModel[],
  ): void {
    const data = {
      ...inputs,
      ...(previewProducts ? { products: [...previewProducts] } : {}),
    };
    this.modalsService.open(KioskSuggestionsModalComponent.handle, undefined, data);
    this.modalsService.setData(KioskSuggestionsModalComponent.handle, data);
  }

  private closeSharedPreviewModalIfIdle(): void {
    if (isAnyKioskSuggestionsPreviewModalOpen(this.cb2PreviewState.previewScenarios())) {
      return;
    }
    this.modalsService.close(KioskSuggestionsModalComponent.handle);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskSuggestionsModalBlockComponent, {
  'keyAliases': {},
});
